import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';

import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import {
  ReactSelectGroup,
  ReactSelectRefLabelOption,
} from '@/modules/core/reactSelect/types/ReactSelect';
import filter from 'lodash.filter';
import find from 'lodash.find';
import flatten from 'lodash.flatten';
import map from 'lodash.map';
import size from 'lodash.size';
import some from 'lodash.some';
import sortBy from 'lodash.sortby';
import { getValueFallback } from '../core/data/record';
import {
  DEFAULT_STEP_FULL_LABEL_OPTIONS,
  EducationalProgramStepFullLabelOptions,
} from '../core/data/types/EducationalProgramStepFullLabelOptions';

/**
 * Extract all steps in a given set of programs, and return them all as a unified array (flatten).
 *
 * @param {Array<EducationalProgram>} educationalPrograms
 * @return {Array<EducationalProgramStep>}
 */
export const flattenStepsFromEducationalPrograms = (educationalPrograms: Array<EducationalProgram>): Array<EducationalProgramStep> => {
  return flatten(map(educationalPrograms, (educationalProgram: EducationalProgram) => {
    return educationalProgram?.steps || [];
  }));
};

/**
 * Filters out all steps that don't belong to the provided programs
 *
 * @param {EducationalProgramStep[]} educationalProgramSteps
 * @param {EducationalProgram[]} programs
 * @return {EducationalProgramStep[]}
 */
export const filterStepsByPrograms = (educationalProgramSteps: EducationalProgramStep[], programs: Pick<EducationalProgram, 'ref'>[]): EducationalProgramStep[] => {
  return filter(educationalProgramSteps, (step: EducationalProgramStep) => {
    return find(programs, { ref: step?.program?.ref });
  });
};

/**
 * Basically groups a set of steps into a set of programs, each composed on its own steps
 *
 * @param {EducationalProgramStep[]} educationalProgramSteps
 * @return {EducationalProgram[]}
 */
export const groupStepsByPrograms = (educationalProgramSteps: EducationalProgramStep[]): EducationalProgram[] => {
  const educationalPrograms: EducationalProgram[] = [];

  map(educationalProgramSteps, (step: EducationalProgramStep) => {
    // Avoids duplicating programs
    if (!find(educationalPrograms, { ref: step?.program?.ref })) {
      educationalPrograms.push(step?.program);
    }
  });

  return educationalPrograms;
};

/**
 * Converts an array of steps into a grouped array of programs
 *
 * @param {EducationalProgramStep[]} steps
 * @param {EducationalProgram[]} programs
 */
export const convertStepsToStepsGroupedByProgramsAsSelectOptions = (steps: EducationalProgramStep[], programs: Pick<EducationalProgram, 'ref'>[]): ReactSelectGroup[] => {
  return map(
    groupStepsByPrograms(filterStepsByPrograms(steps, programs)),
    (program: EducationalProgram) => {
      return {
        label: program?.label,
        labelShort: program?.labelShort,
        options: map(sortBy(program?.steps, 'label'), (step: EducationalProgramStep): ReactSelectRefLabelOption => {
          return {
            ref: step.ref,
            label: step.label,
          };
        }),
      };
    },
  );
};

/**
 * Check if any of the educationalProgramSteps belong to the educationalProgram
 *
 * @param {EducationalProgram} educationalProgram
 * @param {EducationalProgramStep[]} educationalProgramSteps
 * @return {boolean}
 */
export const doesEducationalProgramContainsAnyStep = (educationalProgram: EducationalProgram, educationalProgramSteps: EducationalProgramStep[]): boolean => {
  return some(educationalProgramSteps, (educationalProgramStep: EducationalProgramStep) => {
    const educationalProgramStepProgramId = educationalProgramStep?.program?.id;
    return educationalProgramStepProgramId === educationalProgram?.id;
  });
};

/**
 * Returns the full label of a step, which includes its program's label.
 *
 * @param {EducationalProgramStep} step
 * @param options
 * @param {string} sep
 * @return {string}
 */
export const getStepFullLabel = (step: EducationalProgramStep, options: EducationalProgramStepFullLabelOptions = {}, sep = ' - '): string => {
  options = { ...DEFAULT_STEP_FULL_LABEL_OPTIONS, ...options }; // Apply default

  const programLabel: string = getValueFallback([
    {
      record: step,
      key: options.preferShortProgramLabel ? 'program.labelShort' : 'program.label',
    },
    {
      record: step,
      key: 'program.label',
    },
  ], '');

  const stepLabel = getValueFallback([
    {
      record: step,
      key: options.preferShortProgramStepLabel ? 'labelShort' : 'label',
    },
    {
      record: step,
      key: 'label',
    },
  ], '');

  return `${programLabel}${sep}${stepLabel}`;
};

/**
 * A step tuition fees should be displayed if a description exists, or if it contains documents. Otherwise, it mustn't (nothing useful to display)
 *
 * @param {EducationalProgramStep} educationalProgramStep
 * @return {boolean}
 */
export const shouldDisplayStepTuitionFees = (educationalProgramStep: EducationalProgramStep): boolean => {
  const educationalProgramStepTuitionFeesDescription: string = educationalProgramStep?.tuitionFeesDescription;
  const educationalProgramStepTuitionFeesDocuments = educationalProgramStep?.tuitionFeesDocuments;

  return !!(size(educationalProgramStepTuitionFeesDescription) || size(educationalProgramStepTuitionFeesDocuments));
};
